import {
  Condition,
  CustomConditionModel,
  DepositPayable,
  FormCondition,
  RenderedSpecialCondition,
  SpecialConditionData,
  SpecialConditionDataConditionType,
} from '@urbanx/agx.campaigns.client';
import { isActionableSpecialCondition } from '../../specialConditionDueDate';

export type CustomSpecialConditionMode = 'without-subclauses' | 'with-subclauses';
export type CustomConditionUpdater = (condition: CustomConditionModel) => CustomConditionModel;

export interface CustomSpecialConditionDrafts {
  withoutSubclauses: SpecialConditionData;
  withSubclauses: SpecialConditionData;
}

export interface CustomSpecialConditionValidation {
  isValid: boolean;
  nameError?: string;
  clauseNumberError?: string;
}

export const CUSTOM_SPECIAL_CONDITION_NAME_MAX_LENGTH = 50;

const createCustomCondition = (
  clauseNumber: number,
  withSubclauses: boolean
): SpecialConditionData => ({
  id: window.crypto.randomUUID(),
  conditionType: SpecialConditionDataConditionType.Custom,
  overriddenClauseNumber: clauseNumber,
  customCondition: {
    clauseId: window.crypto.randomUUID(),
    textInput: '',
    subClauses: withSubclauses
      ? [
          {
            clauseId: window.crypto.randomUUID(),
            textInput: '',
          },
        ]
      : [],
  },
});

export const getUsedCustomSpecialConditionClauseNumbers = (
  existingConditions: SpecialConditionData[],
  renderedConditions: RenderedSpecialCondition[] = []
): number[] => {
  const activeConditionIds = new Set(
    existingConditions
      .map((condition) => condition.id)
      .filter((conditionId): conditionId is string => !!conditionId)
  );
  const hiddenConditionIds = new Set(
    renderedConditions
      .filter((condition) => !isActionableSpecialCondition(condition))
      .map((condition) => condition.id)
      .filter((conditionId): conditionId is string => !!conditionId)
  );

  return [
    ...new Set([
      ...renderedConditions.flatMap((condition) => {
        if (
          !condition.id ||
          !activeConditionIds.has(condition.id) ||
          hiddenConditionIds.has(condition.id)
        )
          return [];

        const clauseNumber = Number(condition.position);
        return Number.isInteger(clauseNumber) ? [clauseNumber] : [];
      }),
      ...existingConditions.flatMap((condition) =>
        condition.overriddenClauseNumber && (!condition.id || !hiddenConditionIds.has(condition.id))
          ? [condition.overriddenClauseNumber]
          : []
      ),
    ]),
  ];
};

export const getNextCustomSpecialConditionClauseNumber = (usedClauseNumbers: number[]): number => {
  return usedClauseNumbers.length === 0 ? 1 : Math.max(...usedClauseNumbers) + 1;
};

export const createCustomSpecialConditionDrafts = (
  clauseNumber: number
): CustomSpecialConditionDrafts => ({
  withoutSubclauses: createCustomCondition(clauseNumber, false),
  withSubclauses: createCustomCondition(clauseNumber, true),
});

export const getActiveCustomSpecialCondition = (
  drafts: CustomSpecialConditionDrafts,
  mode: CustomSpecialConditionMode
): SpecialConditionData =>
  mode === 'without-subclauses' ? drafts.withoutSubclauses : drafts.withSubclauses;

const prepareCustomConditionDueDateForSave = (
  dueDate: FormCondition | undefined
): FormCondition | undefined => {
  if (!dueDate) return undefined;

  const { depositPayable, ...preparedDueDate } = dueDate;

  return {
    ...preparedDueDate,
    condition:
      depositPayable === DepositPayable.NDaysFrom && preparedDueDate.condition === Condition.OnDate
        ? Condition.NdaysFromDate
        : preparedDueDate.condition,
  };
};

const prepareCustomConditionForSave = (
  customCondition: CustomConditionModel
): CustomConditionModel => {
  return {
    ...customCondition,
    overriddenDueDate: prepareCustomConditionDueDateForSave(customCondition.overriddenDueDate),
    subClauses: customCondition.subClauses?.map(prepareCustomConditionForSave),
  };
};

export const prepareCustomSpecialConditionsForSave = (
  conditions: SpecialConditionData[]
): SpecialConditionData[] =>
  conditions.map((condition) =>
    condition.conditionType === SpecialConditionDataConditionType.Custom &&
    condition.customCondition
      ? {
          ...condition,
          customCondition: prepareCustomConditionForSave(condition.customCondition),
        }
      : condition
  );

export const hasValidSpecialConditionDueDate = (dueDate: FormCondition | undefined): boolean => {
  if (!dueDate?.condition) return false;

  if (
    dueDate.depositPayable === DepositPayable.NDaysFrom ||
    dueDate.condition === Condition.NdaysFromDate
  ) {
    return !!dueDate.daysFromDate && dueDate.daysFromDate > 0;
  }

  if (dueDate.condition === Condition.OnSpecificDate) {
    return !!dueDate.referenceDate;
  }

  if (dueDate.condition === Condition.UponSatisfactionOfSpecialCondition) {
    return !!dueDate.specialConditionId;
  }

  return true;
};

// This function validates a custom special condition. The backend owns the domain authority
// for validating special conditions, but this provides a first layer of validation to improve
// user experience.
export const validateCustomSpecialCondition = (
  condition: SpecialConditionData,
  usedClauseNumbers: number[]
): CustomSpecialConditionValidation => {
  const customCondition = condition.customCondition;
  const name = customCondition?.textInput?.trim() ?? '';
  const clauseNumber = condition.overriddenClauseNumber;

  const nameError =
    name.length === 0
      ? 'Enter special condition name'
      : name.length > CUSTOM_SPECIAL_CONDITION_NAME_MAX_LENGTH
        ? `Special condition name must be ${CUSTOM_SPECIAL_CONDITION_NAME_MAX_LENGTH} characters or fewer`
        : undefined;

  const clauseNumberError =
    !clauseNumber || !Number.isInteger(clauseNumber) || clauseNumber < 1
      ? 'Enter a positive whole clause number'
      : usedClauseNumbers.includes(clauseNumber)
        ? 'Clause number is already in use'
        : undefined;

  const subClauses = customCondition?.subClauses ?? [];
  const hasValidContent =
    subClauses.length === 0
      ? hasValidSpecialConditionDueDate(customCondition?.overriddenDueDate)
      : !customCondition?.overriddenDueDate &&
        subClauses.every(
          (subClause) =>
            !!subClause.textInput?.trim() &&
            hasValidSpecialConditionDueDate(subClause.overriddenDueDate)
        );

  return {
    isValid: !nameError && !clauseNumberError && hasValidContent,
    nameError,
    clauseNumberError,
  };
};
